import { useState } from 'react';
import { useStoreState } from 'hooks';
import { useLogin } from 'store/auth';
import { Rule, RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { notification } from 'antd';
import axios from 'axios';
import {
  ExportReportType,
  exportReportToDM,
  ExportReportToDMPayload,
  ExportReportToDMMetadata,
  getSchemaTypeForRule,
  shouldRenewTokenForDV,
} from 'utils/data-validation';

export type ExportReportToDMOptions = {
  exportType?: ExportReportType;
  ruleRuns?: RuleRunStatus[];
  runIds?: string[];
  metadata?: Record<string, { rule?: Rule | null; run?: RuleRunStatus | null }>;
};

function buildRunMetadata(
  rule?: Rule | null,
  run?: RuleRunStatus | null,
  fallbackRunId?: string
): ExportReportToDMMetadata | undefined {
  if (!run && !fallbackRunId) return undefined;
  const schemaType = rule ? getSchemaTypeForRule(rule) : undefined;
  return {
    ruleExternalId: rule?.externalId ?? run?.externalId ?? fallbackRunId,
    schemaType: schemaType ?? undefined,
    runAt: run?.runAt ? new Date(run.runAt).toISOString() : undefined,
    errorMessage: rule?.errorMessage ?? undefined,
    customerDefinedId: rule?.customerDefinedId ?? undefined,
    collectionExternalId: rule?.milestones?.items?.[0]?.externalId ?? undefined,
  };
}

export const useExportReportToDM = (): {
  exportReportToDM: (options: ExportReportToDMOptions) => Promise<void>;
  exportingReport: boolean;
} => {
  let token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);
  const expiry = useStoreState((state) => state.auth.expiry);
  const login = useLogin();

  const [exportingReport, setExporting] = useState(false);

  const doExport = async (options: ExportReportToDMOptions) => {
    let { runIds, metadata } = options;
    const exportType = options.exportType ?? 'data-quality';
    const { ruleRuns } = options;

    if (ruleRuns) {
      runIds =
        runIds ||
        ruleRuns
          .map((r) => r.externalId)
          .filter((id): id is string => Boolean(id));
      metadata =
        metadata ||
        ruleRuns.reduce(
          (acc, run) => {
            if (run?.externalId) {
              acc[run.externalId] = {
                rule: run.rule ?? undefined,
                run,
              };
            }
            return acc;
          },
          {} as Record<
            string,
            { rule?: Rule | null; run?: RuleRunStatus | null }
          >
        );
    }

    if (!runIds?.length) {
      notification.warning({
        message: 'Export Report to DM',
        description: 'No runs selected.',
      });
      return;
    }

    setExporting(true);
    try {
      if (shouldRenewTokenForDV(expiry)) {
        token = (await login({ tenant, useOIDC: true, forceRefresh: true }))
          .token;
      }

      const payloadMetadata: ExportReportToDMPayload['metadata'] = {};
      if (metadata) {
        runIds.forEach((runId) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const m = metadata![runId];
          if (m) {
            const runMeta = buildRunMetadata(m?.rule, m?.run, runId);
            if (runMeta) payloadMetadata[runId] = runMeta;
          } else {
            const runMeta = buildRunMetadata(undefined, undefined, runId);
            if (runMeta) payloadMetadata[runId] = runMeta;
          }
        });
      }
      const payload: ExportReportToDMPayload = {
        runIds,
        exportType,
        metadata: Object.keys(payloadMetadata).length
          ? payloadMetadata
          : undefined,
      };

      const result = await exportReportToDM(payload, token, tenant, cluster);
      if (result.errors?.length) {
        notification.warning({
          message: 'Export Report to DM completed with errors',
          description: `${result.exported} exported.\n${result.errors.join('\n')}`,
          duration: 6,
        });
      } else {
        notification.success({
          message: 'Export Report to DM',
          description: `Successfully exported ${result.exported} report(s) to the data model.`,
        });
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        notification.error({
          message: 'Export Report to DM failed',
          description:
            err.response?.data?.error ?? err.message ?? 'Unknown error',
        });
      } else {
        notification.error({
          message: 'Export Report to DM failed',
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    } finally {
      setExporting(false);
    }
  };

  return { exportReportToDM: doExport, exportingReport };
};
